export function extractDominantColors (imageUrl, count = 3) {
  return new Promise(resolve => {
    const img = new Image()
    img.crossOrigin = 'anonymous'

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        const maxDimension = 200
        let width = img.width
        let height = img.height

        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height)
          width = Math.floor(width * ratio)
          height = Math.floor(height * ratio)
        }

        canvas.width = width
        canvas.height = height

        ctx.drawImage(img, 0, 0, width, height)

        const imageData = ctx.getImageData(0, 0, width, height)
        const pixels = imageData.data

        const colorMap = new Map()
        const sampleStep = 5

        for (let i = 0; i < pixels.length; i += sampleStep * 4) {
          const r = pixels[i]
          const g = pixels[i + 1]
          const b = pixels[i + 2]
          const a = pixels[i + 3]

          if (a < 128) continue

          const brightness = (r + g + b) / 3
          if (brightness < 20 || brightness > 235) continue

          const saturation = Math.max(r, g, b) - Math.min(r, g, b)
          if (saturation < 10) continue

          const quantizedR = Math.floor(r / 32) * 32
          const quantizedG = Math.floor(g / 32) * 32
          const quantizedB = Math.floor(b / 32) * 32

          const key = `${quantizedR},${quantizedG},${quantizedB}`
          colorMap.set(key, (colorMap.get(key) || 0) + 1)
        }

        const sortedColors = Array.from(colorMap.entries())
          .sort((a, b) => b[1] - a[1])
          .slice(0, count)
          .map(([color]) => {
            const [r, g, b] = color.split(',').map(Number)
            return `rgb(${r}, ${g}, ${b})`
          })

        resolve(
          sortedColors.length > 0
            ? sortedColors
            : ['rgb(239, 231, 221)', 'rgb(255, 245, 234)']
        )
      } catch (error) {
        console.warn('Color extraction error:', error)
        resolve(['rgb(239, 231, 221)', 'rgb(255, 245, 234)'])
      }
    }

    img.onerror = () => {
      resolve(['rgb(239, 231, 221)', 'rgb(255, 245, 234)'])
    }

    img.src = imageUrl
  })
}

export function rgbToHex (rgb) {
  const match = rgb.match(/\d+/g)
  if (!match || match.length < 3) return '#EFE7DD'

  const r = parseInt(match[0])
  const g = parseInt(match[1])
  const b = parseInt(match[2])

  return (
    '#' +
    [r, g, b]
      .map(x => {
        const hex = x.toString(16)
        return hex.length === 1 ? '0' + hex : hex
      })
      .join('')
  )
}

export function getLuminance (rgb) {
  const match = rgb.match(/\d+/g)
  if (!match || match.length < 3) return 0

  const r = parseInt(match[0]) / 255
  const g = parseInt(match[1]) / 255
  const b = parseInt(match[2]) / 255

  const [rLinear, gLinear, bLinear] = [r, g, b].map(val => {
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4)
  })

  return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear
}

export function lightenColor (rgb, factor = 0.3) {
  const match = rgb.match(/\d+/g)
  if (!match || match.length < 3) return rgb

  const r = Math.min(
    255,
    Math.floor(parseInt(match[0]) + (255 - parseInt(match[0])) * factor)
  )
  const g = Math.min(
    255,
    Math.floor(parseInt(match[1]) + (255 - parseInt(match[1])) * factor)
  )
  const b = Math.min(
    255,
    Math.floor(parseInt(match[2]) + (255 - parseInt(match[2])) * factor)
  )

  return `rgb(${r}, ${g}, ${b})`
}

export function ensureMinimumBrightness (rgb, minLuminance = 0.6) {
  const luminance = getLuminance(rgb)
  
  if (luminance >= minLuminance) {
    return rgb
  }

  const match = rgb.match(/\d+/g)
  if (!match || match.length < 3) return rgb

  let r = parseInt(match[0])
  let g = parseInt(match[1])
  let b = parseInt(match[2])
  
  let currentLuminance = getLuminance(`rgb(${r}, ${g}, ${b})`)
  let factor = 0.1
  
  while (currentLuminance < minLuminance && factor < 1) {
    r = Math.min(255, Math.floor(r + (255 - r) * factor))
    g = Math.min(255, Math.floor(g + (255 - g) * factor))
    b = Math.min(255, Math.floor(b + (255 - b) * factor))
    currentLuminance = getLuminance(`rgb(${r}, ${g}, ${b})`)
    factor += 0.1
  }

  return `rgb(${r}, ${g}, ${b})`
}

export function createGradient (colors) {
  if (!colors || colors.length === 0) {
    return 'linear-gradient(135deg, #EFE7DD 0%, #FFF5EA 100%)'
  }

  const lightenedColors = colors.map(color => {
    const lightened = lightenColor(color, 0.4)
    return ensureMinimumBrightness(lightened, 0.6)
  })

  if (lightenedColors.length === 1) {
    return `linear-gradient(135deg, ${lightenedColors[0]} 0%, ${lightenedColors[0]} 100%)`
  }

  const stops = lightenedColors
    .map((color, index) => {
      const percent = (index / (lightenedColors.length - 1)) * 100
      return `${color} ${percent}%`
    })
    .join(', ')

  return `linear-gradient(135deg, ${stops})`
}
